import { XMarkIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import { useTruthSocialNotificationSettings } from "../hooks/useTruthSocialNotificationSettings";
import ToggleSwitch from "./ToggleSwitch";

/**
 * Sound-notification filters for Truth Social: a burst throttle, a keyword
 * allowlist and content-type skips. All stored locally. Only gates the beep —
 * the feed still shows every story. Disabled when the master Truth Social sound
 * toggle (passed in) is off.
 */
export default function TruthSocialNotificationControls({
  disabled,
}: {
  disabled: boolean;
}) {
  const { settings, updateSettings } = useTruthSocialNotificationSettings();
  const [keywordDraft, setKeywordDraft] = useState("");

  const addKeyword = (raw: string) => {
    const term = raw.trim();
    if (term === "") return;
    if (
      settings.keywords.some((k) => k.toLowerCase() === term.toLowerCase())
    ) {
      setKeywordDraft("");
      return;
    }
    updateSettings({ keywords: [...settings.keywords, term] });
    setKeywordDraft("");
  };

  const removeKeyword = (term: string) => {
    updateSettings({
      keywords: settings.keywords.filter((k) => k !== term),
    });
  };

  const handleKeywordKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addKeyword(keywordDraft);
    }
  };

  return (
    <div
      className={`px-4 pb-8 sm:px-6 lg:px-8 ${
        disabled ? "pointer-events-none opacity-40" : ""
      }`}
    >
      <div className="max-w-7xl space-y-6 rounded-lg border border-white/10 bg-white/5 p-6">
        <p className="text-sm/6 text-gray-400">
          Trump posts to Truth Social in large batches. These filters quieten
          the notification sound only — every story still appears in the feed.
          Settings are saved on this device.
        </p>

        {/* Burst throttle */}
        <div className="border-t border-white/10 pt-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-sm font-semibold text-white">
                Limit notifications during bursts
              </h3>
              <p className="mt-1 text-sm/6 text-gray-400">
                Play a sound for the first few truths, then go quiet for a while.
              </p>
            </div>
            <ToggleSwitch
              checked={settings.throttleEnabled}
              onChange={(value) => updateSettings({ throttleEnabled: value })}
              srLabel="Enable/Disable burst throttle"
            />
          </div>
          {settings.throttleEnabled && (
            <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-gray-300">
              <span>Notify for the first</span>
              <input
                type="number"
                min={1}
                max={20}
                value={settings.burstCount}
                onChange={(e) =>
                  updateSettings({
                    burstCount: Math.max(1, Number(e.target.value) || 1),
                  })
                }
                className="w-16 rounded-md border border-white/10 bg-white/5 px-2 py-1 text-center text-white focus:border-squawk-blue focus:outline-none"
              />
              <span>truths, then pause for</span>
              <input
                type="number"
                min={1}
                max={120}
                value={settings.cooldownMinutes}
                onChange={(e) =>
                  updateSettings({
                    cooldownMinutes: Math.max(1, Number(e.target.value) || 1),
                  })
                }
                className="w-16 rounded-md border border-white/10 bg-white/5 px-2 py-1 text-center text-white focus:border-squawk-blue focus:outline-none"
              />
              <span>minutes.</span>
            </div>
          )}
        </div>

        {/* Keyword allowlist */}
        <div className="border-t border-white/10 pt-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-sm font-semibold text-white">
                Only notify for keywords
              </h3>
              <p className="mt-1 text-sm/6 text-gray-400">
                When enabled, only truths containing one of these terms will play
                a sound.
              </p>
            </div>
            <ToggleSwitch
              checked={settings.keywordFilterEnabled}
              onChange={(value) =>
                updateSettings({ keywordFilterEnabled: value })
              }
              srLabel="Enable/Disable keyword allowlist"
            />
          </div>
          {settings.keywordFilterEnabled && (
            <div className="mt-4 space-y-3">
              <input
                type="text"
                value={keywordDraft}
                onChange={(e) => setKeywordDraft(e.target.value)}
                onKeyDown={handleKeywordKeyDown}
                onBlur={() => addKeyword(keywordDraft)}
                placeholder="Type a term and press Enter (e.g. tariff, Fed, Powell)"
                className="w-full max-w-md rounded-md border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-gray-500 focus:border-squawk-blue focus:outline-none"
              />
              {settings.keywords.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {settings.keywords.map((term) => (
                    <span
                      key={term}
                      className="inline-flex items-center gap-1.5 rounded-full border border-squawk-blue/50 bg-squawk-blue/20 px-3 py-1 text-xs text-white"
                    >
                      {term}
                      <button
                        type="button"
                        onClick={() => removeKeyword(term)}
                        className="bg-transparent transition-colors hover:text-red-400"
                      >
                        <XMarkIcon className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Content-type skips */}
        <div className="border-t border-white/10 pt-6">
          <h3 className="text-sm font-semibold text-white">
            Skip noisy post types
          </h3>
          <div className="mt-4 space-y-4">
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm/6 text-gray-400">
                Skip retweets (titles starting with “RT @…”)
              </p>
              <ToggleSwitch
                checked={settings.skipRetweets}
                onChange={(value) => updateSettings({ skipRetweets: value })}
                srLabel="Skip retweets"
              />
            </div>
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm/6 text-gray-400">
                Skip image/video-only posts (“Image or video posted”)
              </p>
              <ToggleSwitch
                checked={settings.skipMediaOnly}
                onChange={(value) => updateSettings({ skipMediaOnly: value })}
                srLabel="Skip image or video only posts"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
